import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Application } from '@enums/application.enums';

@Injectable({
  providedIn: 'root'
})
export class HistoryService {
  private history: string[] = [];
  private readonly MAX_HISTORY_LENGTH = 15; // Store only the last 15 entries

  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    if (isPlatformBrowser(this.platformId)) {
      const storedHistory = sessionStorage.getItem('navigationHistory');
      if (storedHistory) {
        try {
          this.history = JSON.parse(storedHistory) || [];
          if (this.history.length > 0) {
            this.history.splice(this.history.length - 1, 1); 
          }
        } catch (error) {
          // If storedHistory is not valid JSON, clear it and start fresh
          console.warn('Invalid navigation history format, clearing stored history:', error);
          this.history = [];
        }
        sessionStorage.removeItem('navigationHistory');
      }
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.addToHistory(event.urlAfterRedirects);
        }
      });

      window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this));
    }
  }

  private getUrlPath(url: string): string {
    return url.split('?')[0].split('#')[0];
  }

  private isAuthHistoryUrl(url: string): boolean {
    const path = this.getUrlPath(url);
    return (
      path === Application.loginPath ||
      path.startsWith(`${Application.loginPath}/`)
    );
  }

  private addToHistory(url: string): void {
    const lastUrl = this.history[this.history.length - 1];
    if (lastUrl && this.getUrlPath(lastUrl) === this.getUrlPath(url)) {
      return;
    }

    this.history.push(url);

    if (this.history.length > this.MAX_HISTORY_LENGTH) {
      this.history.shift();
    }
  }

  private handleBeforeUnload(): void {
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.setItem('navigationHistory', JSON.stringify(this.history));
    }
  }

  // Get all the past navigation history
  getHistory(): string[] {
    return isPlatformBrowser(this.platformId) ? [...this.history] : [];
  }

  // Get the last visited route
  getLastRoute(): string | null {
    return isPlatformBrowser(this.platformId) && this.history.length > 1
      ? this.history[this.history.length - 2]
      : null;
  }

  popAuthRoutes(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    while (
      this.history.length > Application.ZERO &&
      this.isAuthHistoryUrl(this.history[this.history.length - Application.ONE])
    ) {
      this.history.pop();
    }
  }

  // Clear history (optional)
  clearHistory(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.history = [];
      sessionStorage.removeItem('navigationHistory');
    }
  }
}
